import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Users, BookOpen, Brain, Bell, Save, RefreshCw, AlertTriangle, Globe,
} from 'lucide-react';
import { AdminApi } from '../../services/api';

const SECTIONS = [
  { id: 'general', label: 'General', icon: Globe },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'courses', label: 'Courses', icon: BookOpen },
  { id: 'ai', label: 'AI Features', icon: Brain },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
];

const AdminSettingsPanel = ({ token, onNotify }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');
  const [dirty, setDirty] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await AdminApi.getSettings(token);
      if (res.success) {
        setSettings(res.data.settings);
        setDirty(false);
      } else {
        onNotify?.('error', 'Load Failed', res.message || 'Could not load settings.');
      }
    } catch (err) {
      onNotify?.('error', 'Load Failed', err.message || 'Could not load settings.');
    } finally {
      setLoading(false);
    }
  }, [token, onNotify]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateField = (section, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!token || !settings) return;
    setSaving(true);
    try {
      const res = await AdminApi.updateSettings(token, settings);
      if (res.success) {
        setSettings(res.data.settings);
        setDirty(false);
        onNotify?.('success', 'Settings Saved', 'Platform settings updated successfully.');
      } else {
        onNotify?.('error', 'Save Failed', res.message || 'Could not save settings.');
      }
    } catch (err) {
      onNotify?.('error', 'Save Failed', err.message || 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="tab-settings-v2">
        <div className="settings-loading">
          <RefreshCw size={28} className="spin" />
          <p>Loading platform settings…</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="tab-settings-v2">
        <p className="empty-state">Settings unavailable. Try refreshing.</p>
        <button type="button" className="btn-refresh" onClick={fetchSettings}>
          <RefreshCw size={16} /> Retry
        </button>
      </div>
    );
  }

  const g = settings.general || {};
  const um = settings.userManagement || {};
  const cs = settings.courseSettings || {};
  const ai = settings.aiFeatures || {};
  const notif = settings.notifications || {};
  const sec = settings.security || {};

  return (
    <div className="tab-settings-v2">
      <div className="settings-header">
        <div>
          <h2><Shield size={22} /> Platform Settings</h2>
          <p>Configure platform behavior, security, and feature flags</p>
        </div>
        <div className="settings-header__actions">
          <button type="button" className="btn-refresh" onClick={fetchSettings} disabled={saving}>
            <RefreshCw size={16} /> Refresh
          </button>
          <button
            type="button"
            className="btn-save-settings"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? <RefreshCw size={16} className="spin" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="settings-layout">
        <nav className="settings-nav" aria-label="Settings sections">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                className={`settings-nav__btn ${activeSection === s.id ? 'settings-nav__btn--active' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                <Icon size={16} />
                {s.label}
              </button>
            );
          })}
        </nav>

        <div className="settings-panel">
          {activeSection === 'general' && (
            <section className="settings-section">
              <h3>General</h3>
              <div className="settings-form-grid">
                <label className="settings-field">
                  <span>Platform Name</span>
                  <input
                    type="text"
                    value={g.platformName || ''}
                    onChange={(e) => updateField('general', 'platformName', e.target.value)}
                  />
                </label>
                <label className="settings-field">
                  <span>Platform URL</span>
                  <input
                    type="url"
                    value={g.platformUrl || ''}
                    onChange={(e) => updateField('general', 'platformUrl', e.target.value)}
                  />
                </label>
                <label className="settings-field">
                  <span>Support Email</span>
                  <input
                    type="email"
                    value={g.supportEmail || ''}
                    onChange={(e) => updateField('general', 'supportEmail', e.target.value)}
                  />
                </label>
                <label className="settings-field settings-field--full">
                  <span>Description</span>
                  <textarea
                    rows={3}
                    value={g.platformDescription || ''}
                    onChange={(e) => updateField('general', 'platformDescription', e.target.value)}
                  />
                </label>
              </div>
            </section>
          )}

          {activeSection === 'users' && (
            <section className="settings-section">
              <h3>User Management</h3>
              <div className="settings-toggles">
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!um.publicRegistration}
                    onChange={(e) => updateField('userManagement', 'publicRegistration', e.target.checked)}
                  />
                  <span>Allow public registration</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!um.emailVerificationRequired}
                    onChange={(e) => updateField('userManagement', 'emailVerificationRequired', e.target.checked)}
                  />
                  <span>Require email verification</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!um.twoFactorAuthentication}
                    onChange={(e) => updateField('userManagement', 'twoFactorAuthentication', e.target.checked)}
                  />
                  <span>Enable two-factor authentication</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!um.autoApproveInstructors}
                    onChange={(e) => updateField('userManagement', 'autoApproveInstructors', e.target.checked)}
                  />
                  <span>Auto-approve instructor applications</span>
                </label>
              </div>
              <div className="settings-form-grid">
                <label className="settings-field">
                  <span>Password Requirements</span>
                  <select
                    value={um.passwordRequirements || 'medium'}
                    onChange={(e) => updateField('userManagement', 'passwordRequirements', e.target.value)}
                  >
                    <option value="basic">Basic</option>
                    <option value="medium">Medium</option>
                    <option value="strong">Strong</option>
                  </select>
                </label>
                <label className="settings-field">
                  <span>Default User Role</span>
                  <select
                    value={um.defaultUserRole || 'student'}
                    onChange={(e) => updateField('userManagement', 'defaultUserRole', e.target.value)}
                  >
                    <option value="student">Student</option>
                    <option value="instructor">Instructor</option>
                    <option value="mentor">Mentor</option>
                  </select>
                </label>
                <label className="settings-field">
                  <span>Session Timeout</span>
                  <select
                    value={um.sessionTimeout || '8h'}
                    onChange={(e) => updateField('userManagement', 'sessionTimeout', e.target.value)}
                  >
                    <option value="1h">1 hour</option>
                    <option value="4h">4 hours</option>
                    <option value="8h">8 hours</option>
                    <option value="24h">24 hours</option>
                    <option value="never">Never</option>
                  </select>
                </label>
              </div>
            </section>
          )}

          {activeSection === 'courses' && (
            <section className="settings-section">
              <h3>Course Settings</h3>
              <div className="settings-toggles">
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!cs.autoPublish}
                    onChange={(e) => updateField('courseSettings', 'autoPublish', e.target.checked)}
                  />
                  <span>Auto-publish approved courses</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!cs.reviewsRequired}
                    onChange={(e) => updateField('courseSettings', 'reviewsRequired', e.target.checked)}
                  />
                  <span>Require admin review before publish</span>
                </label>
              </div>
              <div className="settings-form-grid">
                <label className="settings-field">
                  <span>Max Course Price ($)</span>
                  <input
                    type="number"
                    min="0"
                    value={cs.maxCoursePrice ?? 999}
                    onChange={(e) => updateField('courseSettings', 'maxCoursePrice', Number(e.target.value))}
                  />
                </label>
                <label className="settings-field">
                  <span>Platform Commission (%)</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={cs.commissionRate ?? 30}
                    onChange={(e) => updateField('courseSettings', 'commissionRate', Number(e.target.value))}
                  />
                </label>
              </div>
            </section>
          )}

          {activeSection === 'ai' && (
            <section className="settings-section">
              <h3>AI Features</h3>
              <div className="settings-toggles">
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!ai.aiTutoringEnabled}
                    onChange={(e) => updateField('aiFeatures', 'aiTutoringEnabled', e.target.checked)}
                  />
                  <span>AI tutoring enabled</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!ai.adaptiveLearningPaths}
                    onChange={(e) => updateField('aiFeatures', 'adaptiveLearningPaths', e.target.checked)}
                  />
                  <span>Adaptive learning paths</span>
                </label>
              </div>
              <div className="settings-form-grid">
                <label className="settings-field">
                  <span>AI Response Language</span>
                  <select
                    value={ai.aiResponseLanguage || 'auto'}
                    onChange={(e) => updateField('aiFeatures', 'aiResponseLanguage', e.target.value)}
                  >
                    <option value="auto">Auto-detect</option>
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="zh">Chinese</option>
                  </select>
                </label>
                <label className="settings-field">
                  <span>Analytics Depth</span>
                  <select
                    value={ai.analyticsDepth || 'detailed'}
                    onChange={(e) => updateField('aiFeatures', 'analyticsDepth', e.target.value)}
                  >
                    <option value="basic">Basic</option>
                    <option value="detailed">Detailed</option>
                    <option value="comprehensive">Comprehensive</option>
                  </select>
                </label>
              </div>
            </section>
          )}

          {activeSection === 'notifications' && (
            <section className="settings-section">
              <h3>Notifications</h3>
              <div className="settings-toggles">
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!notif.emailNotifications}
                    onChange={(e) => updateField('notifications', 'emailNotifications', e.target.checked)}
                  />
                  <span>Email notifications</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!notif.pushNotifications}
                    onChange={(e) => updateField('notifications', 'pushNotifications', e.target.checked)}
                  />
                  <span>Push notifications</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!notif.weeklyDigest}
                    onChange={(e) => updateField('notifications', 'weeklyDigest', e.target.checked)}
                  />
                  <span>Weekly digest emails</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!notif.marketingEmails}
                    onChange={(e) => updateField('notifications', 'marketingEmails', e.target.checked)}
                  />
                  <span>Marketing emails</span>
                </label>
              </div>
            </section>
          )}

          {activeSection === 'security' && (
            <section className="settings-section">
              <h3>Security</h3>
              {sec.maintenanceMode && (
                <div className="settings-alert">
                  <AlertTriangle size={18} />
                  <span>Maintenance mode is currently enabled.</span>
                </div>
              )}
              <div className="settings-toggles">
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!sec.maintenanceMode}
                    onChange={(e) => updateField('security', 'maintenanceMode', e.target.checked)}
                  />
                  <span>Maintenance mode</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={!!sec.ipWhitelisting}
                    onChange={(e) => updateField('security', 'ipWhitelisting', e.target.checked)}
                  />
                  <span>IP whitelisting</span>
                </label>
              </div>
              <div className="settings-form-grid">
                <label className="settings-field settings-field--full">
                  <span>Maintenance Message</span>
                  <textarea
                    rows={2}
                    value={sec.maintenanceMessage || ''}
                    onChange={(e) => updateField('security', 'maintenanceMessage', e.target.value)}
                  />
                </label>
                <label className="settings-field">
                  <span>Rate Limit (req/min)</span>
                  <input
                    type="number"
                    min="10"
                    max="1000"
                    value={sec.rateLimit ?? 100}
                    onChange={(e) => updateField('security', 'rateLimit', Number(e.target.value))}
                  />
                </label>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsPanel;